import React, { useEffect, useState, useMemo, useRef, useCallback, ChangeEvent } from "react";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  Autocomplete,
  useMediaQuery,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { TimeField } from "@mui/x-date-pickers/TimeField";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import CalendarTodayOutlined from "@mui/icons-material/CalendarTodayOutlined";
import BuildOutlined from "@mui/icons-material/BuildOutlined";
import EditNoteOutlined from "@mui/icons-material/EditNoteOutlined";
import StickyNote2Outlined from "@mui/icons-material/StickyNote2Outlined";
import AttachFileOutlined from "@mui/icons-material/AttachFileOutlined";
import ErrorOutlineOutlined from "@mui/icons-material/ErrorOutlineOutlined";
import PriorityHighOutlined from "@mui/icons-material/PriorityHighOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import EventFilledIcon from "@mui/icons-material/Event";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import { ChevronRight } from "@mui/icons-material";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import {
  ChangeServiceReport,
  ServiceReportFile,
  ServiceReportFileTypeCode,
  ServiceReportsState,
} from "../models/ServiceReportsModel";
import { CustomerMachineState } from "../models/models";
import {
  createServiceReport,
  changeServiceReport,
  getMachineParts,
  getServiceReports,
  selectServiceReport,
} from "../customerMachineServiceReportsSlice";
import { getMachine } from "../customerMachinerySlice";
import { MachinePartGroup, MachinePartItem } from "../models/ServiceReportsModel";
import { dt } from "../utils/dt";
import { accentGradientDeep } from "../../../shared/themes/redesignTokens";
import { useRedesignSetup } from "../../../shared/hooks/useRedesignSetup";
import useTranslation from "../../../shared/hooks/useTranslation";
import { getMachineDisplayName } from "../../../shared/utils/machineUtils";
import Loader from "../../../shared/components/Loader";

// ═══════════════════════════════════════════════════
//  GROUPED MACHINE PARTS (same as StepMachineAndDetails)
// ═══════════════════════════════════════════════════

const MACHINE_PART_GROUPS = [
  { groupKey: "spindleSystem", parts: [
    { key: "mainSpindle", en: "Main spindle" }, { key: "spindleMotor", en: "Spindle motor" },
    { key: "spindleBearings", en: "Spindle bearings" }, { key: "toolTaperDrawbar", en: "Tool taper / drawbar" },
  ] },
  { groupKey: "axisDrives", parts: [
    { key: "servoMotors", en: "Servo motors" }, { key: "ballScrews", en: "Ball screws" },
    { key: "linearGuidesRails", en: "Linear guides / rails" }, { key: "axisBearings", en: "Axis bearings" },
  ] },
  { groupKey: "cncControl", parts: [
    { key: "controllerHmi", en: "Controller / HMI" }, { key: "ioBoards", en: "I/O boards" },
    { key: "plcModule", en: "PLC module" }, { key: "softwareFirmware", en: "Software / firmware" },
  ] },
  { groupKey: "electrical", parts: [
    { key: "powerSupply", en: "Power supply" }, { key: "cablesWiring", en: "Cables / wiring" },
    { key: "sensorsSwitches", en: "Sensors / switches" }, { key: "contactorsRelays", en: "Contactors / relays" },
  ] },
  { groupKey: "hydraulicSystem", parts: [
    { key: "hydraulicPump", en: "Hydraulic pump" }, { key: "cylinders", en: "Cylinders" },
    { key: "valves", en: "Valves" }, { key: "hosesFittings", en: "Hoses / fittings" },
  ] },
  { groupKey: "pneumaticSystem", parts: [
    { key: "airSystem", en: "Air system" }, { key: "pneumaticValves", en: "Pneumatic valves" },
    { key: "pneumaticCylinders", en: "Pneumatic cylinders" },
  ] },
  { groupKey: "coolantSystem", parts: [
    { key: "coolantPump", en: "Coolant pump" }, { key: "nozzles", en: "Nozzles" },
    { key: "tankFiltration", en: "Tank / filtration" },
  ] },
  { groupKey: "lubrication", parts: [
    { key: "lubricationPump", en: "Lubrication pump" }, { key: "distributionLines", en: "Distribution lines" },
  ] },
  { groupKey: "toolChanger", parts: [
    { key: "atcMechanism", en: "ATC mechanism" }, { key: "toolMagazine", en: "Tool magazine" },
    { key: "gripperArm", en: "Gripper arm" },
  ] },
  { groupKey: "turretLathes", parts: [
    { key: "turretMechanism", en: "Turret mechanism" }, { key: "toolHolders", en: "Tool holders" },
    { key: "indexingMotor", en: "Indexing motor" },
  ] },
  { groupKey: "workholding", parts: [
    { key: "chuckCollet", en: "Chuck / collet" }, { key: "jaws", en: "Jaws" },
    { key: "tailstock", en: "Tailstock" }, { key: "steadyRest", en: "Steady rest" },
  ] },
  { groupKey: "chipManagement", parts: [
    { key: "chipConveyor", en: "Chip conveyor" }, { key: "chipAuger", en: "Chip auger" },
  ] },
  { groupKey: "enclosureSafety", parts: [
    { key: "doorsGuards", en: "Doors / guards" }, { key: "interlocks", en: "Interlocks" },
    { key: "wayCovers", en: "Way covers" },
  ] },
  { groupKey: "consumables", parts: [
    { key: "filters", en: "Filters" }, { key: "belts", en: "Belts" },
    { key: "sealsORings", en: "Seals / O-rings" }, { key: "wipers", en: "Wipers" },
  ] },
  { groupKey: "other", parts: [
    { key: "other", en: "Other" },
  ] },
];

interface PartOption {
  label: string;
  group: string;
  englishName: string;
  categoryId?: string;
}

// ═══════════════════════════════════════════════════
//  FILE HELPERS
// ═══════════════════════════════════════════════════

const getFileIcon = (name: string) => {
  const ext = name.toLowerCase().split(".").pop();
  if (ext === "pdf") return <PictureAsPdfOutlinedIcon sx={{ fontSize: 18, color: "#E74C3C" }} />;
  if (["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp"].includes(ext || ""))
    return <ImageOutlinedIcon sx={{ fontSize: 18, color: dt.blue }} />;
  return <InsertDriveFileOutlinedIcon sx={{ fontSize: 18, color: dt.textSecondary }} />;
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const MAX_FILE_SIZE_MB = 10;

// ═══════════════════════════════════════════════════
//  STYLES
// ═══════════════════════════════════════════════════

const sectionSx = {
  backgroundColor: dt.cardBg,
  borderRadius: dt.radius,
  border: `1px solid ${dt.cardBorder}`,
  boxShadow: dt.cardShadow,
  p: { xs: 2.5, md: 4 },
  display: "flex",
  flexDirection: "column" as const,
  gap: 3,
};

const sectionHeaderSx = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  mb: 1,
};

const sectionLabelSx = {
  fontFamily: dt.font,
  fontSize: "12px",
  fontWeight: 700,
  color: dt.textSecondary,
  textTransform: "uppercase" as const,
  letterSpacing: "0.5px",
};

const sectionIconSx = {
  fontSize: 16,
  color: dt.textMuted,
};

const labelSx = {
  fontFamily: dt.font,
  fontSize: "13px",
  fontWeight: 600,
  color: dt.textPrimary,
  letterSpacing: "0.1px",
  mb: 0.5,
};

// ═══════════════════════════════════════════════════
//  COMPONENT
// ═══════════════════════════════════════════════════

const CreateServiceReportPage = () => {
  useRedesignSetup();

  const { id: machineId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isPhone = useMediaQuery("(max-width:768px)");

  const { translate } = useTranslation(["myMachinery", "servicing", "common"]);

  const { machine } = useAppSelector<CustomerMachineState>((state: any) => state.customerMachinery);
  const { serviceReport, serviceReportsQuery, machineParts } = useAppSelector<ServiceReportsState>(
    (state: any) => state.customerServiceReports
  );

  const isEdit = !!serviceReport;
  const serviceReportNumber = serviceReport?.number || undefined;

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [files, setFiles] = useState<ServiceReportFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [fileError, setFileError] = useState<string>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    watch,
    control,
    handleSubmit,
    reset,
    trigger,
    formState: { errors },
  } = useForm<ChangeServiceReport>();

  const openDate = watch("openDate");

  // ── Load machine + machine parts ──────────────────────────
  useEffect(() => {
    if (machineId) {
      dispatch(getMachine(machineId));
    }
    dispatch(getMachineParts());
  }, [machineId, dispatch]);

  // ── Pre-fill form for edit mode ────────────────────────
  useEffect(() => {
    if (serviceReport) {
      reset({
        title: serviceReport.title,
        description: serviceReport.description,
        notes: serviceReport.notes || "",
        alarmCode: serviceReport.alarmCode || "",
        urgency: serviceReport.urgency,
        openDate: serviceReport.openDate,
        affectedPartCategoryId: serviceReport.affectedPartCategoryId,
        files: serviceReport.files,
      });
      setFiles(serviceReport.files || []);
    } else {
      reset({
        title: "",
        description: "",
        notes: "",
        alarmCode: "",
        urgency: undefined,
        openDate: undefined,
        affectedPartCategoryId: undefined,
        files: null,
      });
      setFiles([]);
    }
  }, [serviceReport, reset]);

  // ── Clean up on unmount ────────────────────────────────
  useEffect(() => {
    return () => {
      dispatch(selectServiceReport(null));
    };
  }, [dispatch]);

  const displayName = machine ? getMachineDisplayName(machine) : "";

  // ── Build grouped part options from Machinery API ────────
  const partOptions: PartOption[] = useMemo(() => {
    const opts: PartOption[] = [];
    if (machineParts && machineParts.length > 0) {
      for (const group of machineParts) {
        const groupLabel = translate(`servicing:machineParts.groups.${group.groupKey}`);
        for (const part of group.parts) {
          // Use code as translation key (e.g., main_spindle -> mainSpindle)
          const translationKey = part.code.replace(/_([a-z])/g, (_m: string, c: string) => c.toUpperCase());
          const partLabel = translate(`servicing:machineParts.parts.${translationKey}`);
          opts.push({ label: partLabel, group: groupLabel, englishName: part.name, categoryId: part.id });
        }
      }
    } else {
      // Fallback to hardcoded groups if API hasn't loaded yet
      for (const group of MACHINE_PART_GROUPS) {
        const groupLabel = translate(`servicing:machineParts.groups.${group.groupKey}`);
        for (const part of group.parts) {
          const partLabel = translate(`servicing:machineParts.parts.${part.key}`);
          opts.push({ label: partLabel, group: groupLabel, englishName: part.en });
        }
      }
    }
    return opts;
  }, [machineParts, translate]);

  // ── File upload handlers ───────────────────────────────
  const readFileAsDataUrl = useCallback(
    (file: File): Promise<ServiceReportFile | null> => {
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        setFileError(translate("common:uploadFile.fileIsTooBig"));
        return Promise.resolve(null);
      }
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
          resolve({ name: file.name, content: reader.result as string, size: file.size, typeCode: ServiceReportFileTypeCode.reportDocument });
        };
        reader.onerror = () => resolve(null);
      });
    },
    [translate]
  );

  const processFiles = useCallback(
    async (fileList: File[]) => {
      setFileError(undefined);
      const results = await Promise.all(fileList.map((f) => readFileAsDataUrl(f)));
      const validFiles = results.filter((f): f is ServiceReportFile => f !== null);
      if (validFiles.length > 0) {
        setFiles((prev) => [...prev, ...validFiles]);
      }
    },
    [readFileAsDataUrl]
  );

  const handleFileDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      if (e.dataTransfer.files.length > 0) {
        processFiles(Array.from(e.dataTransfer.files));
      }
    },
    [processFiles]
  );

  const handleFileInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        processFiles(Array.from(e.target.files));
      }
      e.target.value = "";
    },
    [processFiles]
  );

  const handleRemoveFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // ── Submit ─────────────────────────────────────────────
  const onSubmit: SubmitHandler<ChangeServiceReport> = async (data) => {
    data.openDate = data.openDate || moment().toDate();
    data.files = files;
    if (isEdit && serviceReportNumber) {
      await dispatch(changeServiceReport({ model: data, serviceReportNumber }));
    } else if (machineId) {
      await dispatch(createServiceReport({ model: data, machineId }));
    }
    if (machineId) {
      await dispatch(getServiceReports({ machineId, query: serviceReportsQuery }));
    }
    navigate(`/machinery/${machineId}`);
  };

  // ── Loading ────────────────────────────────────────────
  if (!machine && machineId) {
    return <Loader />;
  }

  // ═══════════════════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════════════════

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100%",
        fontFamily: dt.font,
        "& *:not(.MuiSvgIcon-root)": { fontFamily: `${dt.font}` },
        background: dt.pageBg,
        backgroundImage: `radial-gradient(circle, ${dt.pageDotColor} 1px, transparent 1px)`,
        backgroundSize: "20px 20px",
      }}
    >
      {/* ═══ Header bar ═══ */}
      <Box
        sx={{
          background: "#FFFFFF",
          borderBottom: `1px solid ${dt.cardBorder}`,
          px: isPhone ? 2 : 4,
          py: 2,
          display: "flex",
          alignItems: "center",
          gap: 2,
          animation: "headerReveal 0.5s cubic-bezier(0.4,0,0.2,1) both",
        }}
      >
        <IconButton
          onClick={() => navigate(`/machinery/${machineId}`)}
          sx={{ color: dt.textSecondary, "&:hover": { color: dt.blue, backgroundColor: dt.blueSoft } }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Box sx={{ fontSize: { xs: "16px", md: "18px" }, fontWeight: 700, color: dt.textPrimary }}>
            {isEdit ? translate("myMachinery:srPage.editReportTitle") : translate("myMachinery:srPage.newReportTitle")}
          </Box>
          {/* Breadcrumb */}
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px", mt: 0.25 }}>
            <Box
              onClick={() => navigate("/machinery")}
              sx={{ fontSize: "12px", fontWeight: 500, color: dt.blue, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
            >
              {translate("myMachinery:breadcrumbEquipment")}
            </Box>
            <ChevronRight sx={{ fontSize: 14, color: dt.textMuted }} />
            <Box
              onClick={() => navigate(`/machinery/${machineId}`)}
              sx={{
                fontSize: "12px", fontWeight: 500, color: dt.blue, cursor: "pointer",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "160px",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {displayName}
            </Box>
            <ChevronRight sx={{ fontSize: 14, color: dt.textMuted }} />
            <Box sx={{ fontSize: "12px", fontWeight: 500, color: dt.textMuted }}>
              {isEdit ? translate("myMachinery:editServiceReport") : translate("myMachinery:createServiceReport")}
            </Box>
          </Box>
        </Box>
      </Box>

      {/* ═══ Content area ═══ */}
      <Box
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          px: isPhone ? 2 : 4,
          py: 3,
          pb: 12,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: "720px" }}>
          <Box sx={{ ...sectionSx, animation: "fadeUp 0.5s cubic-bezier(0.4,0,0.2,1) both" }}>
            {/* ── REPORT TITLE ── */}
            <Box>
              <Box sx={sectionHeaderSx}>
                <DescriptionOutlined sx={sectionIconSx} />
                <Box sx={sectionLabelSx}>{translate("myMachinery:reportTitleSection")}</Box>
              </Box>
              <Tooltip title={translate("myMachinery:titleCharLimit")} arrow placement="top-start">
                <TextField
                  fullWidth
                  size="small"
                  placeholder={translate("myMachinery:reportTitle")}
                  {...register("title", {
                    required: translate("common:forms.required"),
                    maxLength: { value: 100, message: translate("myMachinery:titleTooLong") },
                  })}
                  inputProps={{ maxLength: 100 }}
                  error={!!errors.title}
                  helperText={errors.title?.message}
                  sx={{
                    "& .MuiInputBase-input": { fontFamily: dt.fontMono },
                    "& .MuiOutlinedInput-root": {
                      borderRadius: dt.radiusSm,
                      "&.Mui-focused fieldset": { borderColor: dt.blue },
                    },
                  }}
                />
              </Tooltip>
            </Box>

            {/* ── ALARM CODE ── */}
            <Box>
              <Box sx={sectionHeaderSx}>
                <ErrorOutlineOutlined sx={sectionIconSx} />
                <Box sx={sectionLabelSx}>{translate("myMachinery:alarmCodeSection")}</Box>
              </Box>
              <TextField
                fullWidth
                size="small"
                placeholder={translate("myMachinery:alarmCodePlaceholder")}
                {...register("alarmCode", {
                  maxLength: { value: 100, message: translate("myMachinery:alarmCodeTooLong") },
                })}
                inputProps={{ maxLength: 100 }}
                error={!!errors.alarmCode}
                helperText={errors.alarmCode?.message}
                sx={{
                  "& .MuiInputBase-input": { fontFamily: dt.fontMono },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: dt.radiusSm,
                    "&.Mui-focused fieldset": { borderColor: dt.blue },
                  },
                }}
              />
            </Box>

            {/* ── URGENCY ── */}
            <Box>
              <Box sx={sectionHeaderSx}>
                <PriorityHighOutlined sx={sectionIconSx} />
                <Box sx={sectionLabelSx}>{translate("myMachinery:urgencySection")}</Box>
              </Box>
              <FormControl fullWidth size="small">
                <Controller
                  control={control}
                  name="urgency"
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={field.value ?? ""}
                      onChange={(e) => field.onChange(e.target.value === "" ? undefined : Number(e.target.value))}
                      displayEmpty
                      sx={{
                        fontFamily: dt.font,
                        borderRadius: dt.radiusSm,
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: dt.cardBorder },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: dt.blue },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            borderRadius: dt.radiusSm,
                            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                          },
                        },
                      }}
                    >
                      <MenuItem value="" sx={{ fontFamily: dt.font, color: dt.textMuted }}>
                        {translate("myMachinery:urgencyNone")}
                      </MenuItem>
                      <MenuItem value={1} sx={{ fontFamily: dt.font }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#C0392B" }} />
                          {translate("myMachinery:priorities.urgent")}
                        </Box>
                      </MenuItem>
                      <MenuItem value={2} sx={{ fontFamily: dt.font }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#E67E22" }} />
                          {translate("myMachinery:priorities.high")}
                        </Box>
                      </MenuItem>
                      <MenuItem value={4} sx={{ fontFamily: dt.font }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#27AE60" }} />
                          {translate("myMachinery:priorities.low")}
                        </Box>
                      </MenuItem>
                    </Select>
                  )}
                />
              </FormControl>
            </Box>

            {/* ── DATE & TIME ── */}
            <Box>
              <Box sx={sectionHeaderSx}>
                <CalendarTodayOutlined sx={sectionIconSx} />
                <Box sx={sectionLabelSx}>{translate("myMachinery:dateAndTimeSection")}</Box>
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: isPhone ? "1fr" : "1fr 1fr", gap: 2 }}>
                <Box>
                  <InputLabel sx={labelSx}>{translate("myMachinery:openDate")}</InputLabel>
                  <FormControl fullWidth size="small">
                    <Controller
                      control={control}
                      name="openDate"
                      rules={{
                        validate: (value) => {
                          if (value && moment(value) > moment()) {
                            return translate("myMachinery:openDateFuture");
                          }
                        },
                      }}
                      render={({ field }) => (
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                          <DatePicker
                            {...field}
                            value={moment(field.value)}
                            onChange={(value) => field.onChange(value?.toDate())}
                            format="DD.MM.YYYY"
                            disableFuture
                            maxDate={moment()}
                            views={["year", "month", "day"]}
                            open={isCalendarOpen}
                            onOpen={() => setIsCalendarOpen(true)}
                            onClose={() => setIsCalendarOpen(false)}
                            slotProps={{
                              textField: {
                                size: "small",
                                name: "openDate",
                                InputProps: {
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        onClick={() => setIsCalendarOpen(true)}
                                        sx={{ color: dt.blue, "&:hover": { backgroundColor: dt.blueSoft } }}
                                      >
                                        <EventFilledIcon />
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                },
                              },
                              popper: {
                                sx: {
                                  "& .MuiPickersDay-root.Mui-selected": {
                                    backgroundColor: dt.blue,
                                    "&:hover": { backgroundColor: dt.blue },
                                  },
                                },
                              },
                            }}
                          />
                        </LocalizationProvider>
                      )}
                    />
                  </FormControl>
                </Box>
                <Box>
                  <InputLabel sx={labelSx}>{translate("common:common.time")}</InputLabel>
                  <FormControl fullWidth size="small">
                    <Controller
                      control={control}
                      name="openDate"
                      render={({ field }) => (
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                          <TimeField
                            {...field}
                            value={openDate ? moment(openDate) : null}
                            onChange={(value) => {
                              if (value) {
                                const currentDate = openDate ? moment(openDate) : moment();
                                const updatedDate = currentDate
                                  .hour(value.hour())
                                  .minute(value.minute())
                                  .second(0)
                                  .millisecond(0);
                                field.onChange(updatedDate.toDate());
                              }
                            }}
                            format="HH:mm"
                          />
                        </LocalizationProvider>
                      )}
                    />
                  </FormControl>
                </Box>
              </Box>
              <FormHelperText error>{errors.openDate?.message}</FormHelperText>
            </Box>

            {/* ── AFFECTED MACHINE PART (grouped autocomplete) ── */}
            <Box>
              <Box sx={sectionHeaderSx}>
                <BuildOutlined sx={sectionIconSx} />
                <Box sx={sectionLabelSx}>{translate("myMachinery:affectedPartSection")}</Box>
              </Box>
              <FormControl fullWidth>
                <Controller
                  control={control}
                  name="affectedPartCategoryId"
                  rules={{ required: translate("common:forms.required") }}
                  render={({ field }) => {
                    const selectedOption = partOptions.find((o) => o.categoryId === field.value) || null;
                    return (
                      <Autocomplete
                        options={partOptions.filter((o) => !!o.categoryId)}
                        groupBy={(option) => option.group}
                        getOptionLabel={(option) => option.label}
                        value={selectedOption}
                        onChange={(_e, newValue) => {
                          field.onChange(newValue?.categoryId || "");
                          setTimeout(() => trigger("affectedPartCategoryId"));
                        }}
                        isOptionEqualToValue={(option, value) => option.categoryId === value.categoryId}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            error={!!errors.affectedPartCategoryId}
                            placeholder={translate("myMachinery:affectedMachinePart")}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: dt.radiusSm,
                                "&.Mui-focused fieldset": { borderColor: dt.blue },
                              },
                            }}
                          />
                        )}
                        renderGroup={(params) => (
                          <Box key={params.key}>
                            <Box
                              sx={{
                                fontFamily: dt.font,
                                fontSize: "11px",
                                fontWeight: 700,
                                textTransform: "uppercase",
                                letterSpacing: "0.8px",
                                color: dt.textSecondary,
                                px: 2,
                                pt: 1.5,
                                pb: 0.5,
                                position: "sticky",
                                top: 0,
                                backgroundColor: "#fff",
                                zIndex: 1,
                              }}
                            >
                              {params.group}
                            </Box>
                            {params.children}
                          </Box>
                        )}
                        renderOption={(props, option) => (
                          <Box
                            component="li"
                            {...props}
                            sx={{
                              fontFamily: `${dt.font} !important`,
                              fontSize: "14px !important",
                              pl: "32px !important",
                              py: "6px !important",
                              "&:hover": { backgroundColor: `${dt.blueSoft} !important` },
                              "&.Mui-focused": { backgroundColor: `${dt.blueSoft} !important` },
                            }}
                          >
                            {option.label}
                          </Box>
                        )}
                        slotProps={{
                          paper: {
                            sx: {
                              borderRadius: dt.radiusSm,
                              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                              maxHeight: 350,
                            },
                          },
                        }}
                      />
                    );
                  }}
                />
                <FormHelperText error>{errors.affectedPartCategoryId?.message}</FormHelperText>
              </FormControl>
            </Box>

            {/* ── DESCRIPTION ── */}
            <Box>
              <Box sx={sectionHeaderSx}>
                <EditNoteOutlined sx={sectionIconSx} />
                <Box sx={sectionLabelSx}>{translate("myMachinery:descriptionSection")}</Box>
              </Box>
              <Box
                sx={{
                  background: dt.inputBg,
                  borderRadius: dt.radiusSm,
                  border: "1.5px solid transparent",
                  padding: "12px",
                  transition: "all 0.2s ease",
                  "&:focus-within": {
                    border: `1.5px solid ${dt.blue}`,
                    boxShadow: `0 0 0 3px ${dt.blueGlow}`,
                  },
                }}
              >
                <textarea
                  {...register("description")}
                  placeholder={translate("myMachinery:descriptionPlaceholder")}
                  maxLength={2000}
                  rows={6}
                  style={{
                    border: "none",
                    outline: "none",
                    width: "100%",
                    fontSize: "14px",
                    fontFamily: "var(--font-sans)",
                    color: "#1a1a2e",
                    background: "transparent",
                    resize: "vertical",
                    minHeight: "120px",
                    boxSizing: "border-box",
                  }}
                />
              </Box>
              {errors.description && (
                <FormHelperText error>{errors.description?.message}</FormHelperText>
              )}
            </Box>

            {/* ── TECHNICAL NOTES ── */}
            <Box>
              <Box sx={sectionHeaderSx}>
                <StickyNote2Outlined sx={sectionIconSx} />
                <Box sx={sectionLabelSx}>{translate("myMachinery:technicalNotesSection")}</Box>
              </Box>
              <Box
                sx={{
                  background: dt.inputBg,
                  borderRadius: dt.radiusSm,
                  border: "1.5px solid transparent",
                  padding: "12px",
                  transition: "all 0.2s ease",
                  "&:focus-within": {
                    border: `1.5px solid ${dt.blue}`,
                    boxShadow: `0 0 0 3px ${dt.blueGlow}`,
                  },
                }}
              >
                <textarea
                  {...register("notes")}
                  placeholder={translate("myMachinery:technicalNotesPlaceholder")}
                  maxLength={4000}
                  rows={4}
                  style={{
                    border: "none",
                    outline: "none",
                    width: "100%",
                    fontSize: "14px",
                    fontFamily: "var(--font-sans)",
                    color: "#1a1a2e",
                    background: "transparent",
                    resize: "vertical",
                    minHeight: "80px",
                    boxSizing: "border-box",
                  }}
                />
              </Box>
            </Box>

            {/* ── ATTACHMENTS (elevated drop zone) ── */}
            <Box>
              <Box sx={sectionHeaderSx}>
                <AttachFileOutlined sx={sectionIconSx} />
                <Box sx={sectionLabelSx}>{translate("myMachinery:attachmentsSection")}</Box>
              </Box>

              <Box
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true); }}
                onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); }}
                onDrop={handleFileDrop}
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  height: "100px",
                  border: `2px dashed ${fileError ? "#C0392B" : isDragOver ? dt.blue : dt.cardBorder}`,
                  borderRadius: dt.radiusSm,
                  backgroundColor: isDragOver ? dt.blueGlow : dt.inputBg,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "6px",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": { borderColor: dt.blue, backgroundColor: dt.blueSoft },
                }}
              >
                <CloudUploadOutlinedIcon
                  sx={{ fontSize: 28, color: isDragOver ? dt.blue : dt.textMuted, transition: "color 0.2s ease" }}
                />
                <Typography sx={{ fontSize: "13px", color: dt.textSecondary, fontFamily: dt.font }}>
                  {translate("myMachinery:dropFilesHere")}
                </Typography>
                <Box sx={{ fontSize: "11px", color: dt.textMuted, fontFamily: dt.font }}>
                  {translate("myMachinery:supportedFormatsHint")}
                </Box>
              </Box>

              <input
                ref={fileInputRef}
                type="file"
                multiple
                style={{ display: "none" }}
                onChange={handleFileInputChange}
              />

              {fileError && <FormHelperText error sx={{ mt: 0.5 }}>{fileError}</FormHelperText>}

              {files.length > 0 && (
                <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 0.75 }}>
                  {files.map((file, index) => (
                    <Box
                      key={`${file.name}-${index}`}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        p: 1,
                        borderRadius: dt.radiusSm,
                        border: `1px solid ${dt.cardBorder}`,
                        backgroundColor: dt.cardBg,
                        transition: "all 0.2s ease",
                        "&:hover": { backgroundColor: "#FAFAFA", "& .file-delete": { opacity: 1 } },
                        animation: `fadeUp 0.3s ease ${index * 0.05}s both`,
                      }}
                    >
                      {getFileIcon(file.name)}
                      <Typography
                        sx={{
                          fontFamily: dt.font, fontSize: "13px", fontWeight: 500, color: dt.textPrimary,
                          flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0,
                        }}
                      >
                        {file.name}
                      </Typography>
                      {file.size > 0 && (
                        <Typography sx={{ fontFamily: dt.fontMono, fontSize: "11px", fontWeight: 500, color: dt.textMuted, flexShrink: 0 }}>
                          {formatFileSize(file.size)}
                        </Typography>
                      )}
                      <Tooltip title={translate("common:buttons.delete")}>
                        <IconButton
                          className="file-delete"
                          size="small"
                          onClick={(e) => { e.stopPropagation(); handleRemoveFile(index); }}
                          sx={{
                            opacity: 0.5, transition: "opacity 0.2s ease", p: "4px",
                            "&:hover": { backgroundColor: "rgba(220,38,38,0.08)", color: "#C0392B" },
                          }}
                        >
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>

          {/* ── Bottom action row ── */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1.5,
              mt: 3,
              animation: "fadeUp 0.5s cubic-bezier(0.4,0,0.2,1) 0.1s both",
            }}
          >
            <Button
              variant="text"
              onClick={() => navigate(`/machinery/${machineId}`)}
              sx={{
                fontFamily: dt.font, fontWeight: 600, fontSize: "14px", textTransform: "none",
                color: dt.textSecondary, borderRadius: dt.radiusSm, px: 2.5, py: 1,
                transition: "all 0.2s ease",
                "&:hover": { backgroundColor: dt.inputBg, color: dt.textPrimary },
              }}
            >
              {translate("myMachinery:srPage.cancel")}
            </Button>

            <Button
              type="submit"
              variant="contained"
              startIcon={<CheckCircleOutlineIcon sx={{ fontSize: "18px !important" }} />}
              sx={{
                background: dt.serviceReports.createBtnGradient,
                borderRadius: dt.radiusPill,
                fontFamily: dt.font, fontWeight: 600, textTransform: "none",
                px: 3, py: 1,
                boxShadow: "0 2px 8px rgba(52,152,219,0.25)",
                "&:hover": { background: accentGradientDeep, boxShadow: "0 4px 14px rgba(52,152,219,0.35)" },
              }}
            >
              {isEdit ? translate("myMachinery:srPage.saveChanges") : translate("myMachinery:srPage.createReport")}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CreateServiceReportPage;
